"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Calculator, ArrowRight, ArrowRightLeft, BookOpen } from "lucide-react";
import { getCalculatorUrl, extractCalculatorSlug, getActiveCountry } from "@/lib/utils/link-utils";
import { calculatorRegistry } from "@/lib/constants/calculators";
import { comparisonsMap } from "@/data/comparisons";

interface RelatedTool {
  title: string;
  href: string;
  description: string;
  type: "calculator" | "comparison" | "glossary" | "guide";
}

interface RelatedToolsProps {
  tools: RelatedTool[];
  postCountryCode?: string;
  compact?: boolean;
  customTitle?: string;
  customDescription?: string;
  disableBackfill?: boolean;
  hideHeader?: boolean;
}

const TYPE_CONFIG = {
  calculator: {
    label: "Calculator",
    icon: <Calculator size={13} />,
    color: "var(--primary-a12)",
    textColor: "var(--primary)",
  },
  comparison: {
    label: "Compare",
    icon: <ArrowRightLeft size={13} />,
    color: "var(--secondary-a12)",
    textColor: "var(--secondary)",
  },
  glossary: {
    label: "Glossary",
    icon: <BookOpen size={13} />,
    color: "var(--violet-a12)",
    textColor: "var(--violet)",
  },
  guide: {
    label: "Guide",
    icon: <BookOpen size={13} />,
    color: "var(--orange-a12)",
    textColor: "var(--accent-orange)",
  },
};

/**
 * RelatedTools component for blog posts
 * Shows relevant calculators, comparison pages, and individual glossary terms with dynamic country-aware URLs.
 */
export default function RelatedTools({ tools, postCountryCode, compact = false, customTitle, customDescription, disableBackfill = false, hideHeader = false }: RelatedToolsProps) {
  const pathname = usePathname();
  const activeCountry = getActiveCountry(pathname);

  // Prioritize the post's own country code if defined, otherwise fall back to the active country
  const targetCountry = postCountryCode ? postCountryCode.toLowerCase() : activeCountry;

  if (!tools || tools.length === 0) return null;

  // Filter tools to exclude any calculators that are not supported in the target country
  let filteredTools = tools.filter((tool) => {
    if (tool.type === "calculator") {
      const rawSlug = extractCalculatorSlug(tool.href);
      const slug = rawSlug.replace("-calculator", "");
      const calc = calculatorRegistry.find(c => c.slug === slug || c.slug === rawSlug);
      if (!calc) return false;
      return calc.countries === "all" || calc.countries.includes(targetCountry);
    }
    return true;
  });

  // Identify the active category
  let activeCategory = "tax";
  const pathSegments = pathname.split("/").filter(Boolean);
  const categoriesList = ["tax", "loans", "investments", "retirement", "savings", "budgeting", "tools"];
  const categoryFromPath = pathSegments.find(segment => categoriesList.includes(segment.toLowerCase()));
  
  if (categoryFromPath) {
    activeCategory = categoryFromPath.toLowerCase();
  } else {
    // Try to find category from the existing tools in the list
    for (const tool of filteredTools) {
      if (tool.type === "calculator") {
        const rawSlug = extractCalculatorSlug(tool.href);
        const slug = rawSlug.replace("-calculator", "");
        const calc = calculatorRegistry.find(c => c.slug === slug || c.slug === rawSlug);
        if (calc) {
          activeCategory = calc.category.toLowerCase();
          break;
        }
      } else if (tool.type === "comparison") {
        const compSlug = tool.href.split("/").pop() || "";
        const comp = comparisonsMap[compSlug];
        if (comp) {
          activeCategory = comp.category.toLowerCase();
          break;
        }
      }
    }
  }

  // If we have less than 5 tools, backfill with calculators and comparisons from the same category
  const targetCount = 5;
  if (!disableBackfill && filteredTools.length < targetCount) {
    const addedHrefs = new Set(filteredTools.map(t => t.href.toLowerCase()));

    // Pool of matching calculators for the country & category. Entries without
    // a registry description are skipped outright — no fallback templates
    // (PRD 05 §7.1 bans auto-generated related descriptions).
    const currentSlug = pathname.split("/").pop() || "";
    const calcPool = calculatorRegistry.filter(c => {
      if (c.slug === currentSlug) return false;
      if (!c.description || !c.description.trim()) return false;
      const isSameCategory = c.category.toLowerCase() === activeCategory;
      const isSupported = c.countries === "all" || c.countries.includes(targetCountry);
      const href = `/${targetCountry}/${c.category}/${c.slug}`;
      return isSameCategory && isSupported && !addedHrefs.has(href.toLowerCase());
    });

    // Pool of matching comparisons for the country & category (same rule: a
    // missing description excludes the entry instead of templating one).
    const compPool = Object.entries(comparisonsMap).filter(([slug, comp]) => {
      if (!comp.description || !comp.description.trim()) return false;
      const isSameCategory = comp.category.toLowerCase() === activeCategory;
      const isSameCountry = !comp.country || comp.country === "all" || comp.country.toLowerCase() === targetCountry;
      const href = `/compare/${slug}`;
      return isSameCategory && isSameCountry && !addedHrefs.has(href.toLowerCase());
    });

    let calcIndex = 0;
    let compIndex = 0;

    // Alternate adding calculators and comparisons to reach the target count of 5
    while (filteredTools.length < targetCount && (calcIndex < calcPool.length || compIndex < compPool.length)) {
      if (calcIndex < calcPool.length && filteredTools.length < targetCount) {
        const c = calcPool[calcIndex++];
        const href = `/${targetCountry}/${c.category}/${c.slug}`;
        filteredTools.push({
          title: c.name,
          href,
          description: c.description,
          type: "calculator"
        });
        addedHrefs.add(href.toLowerCase());
      }
      if (compIndex < compPool.length && filteredTools.length < targetCount) {
        const [slug, comp] = compPool[compIndex++];
        const href = `/compare/${slug}`;
        filteredTools.push({
          title: comp.title,
          href,
          description: comp.description,
          type: "comparison"
        });
        addedHrefs.add(href.toLowerCase());
      }
    }
  }

  if (filteredTools.length === 0) return null;

  const displayTitle = customTitle || "Related Tools, Comparisons & Glossary";
  const displayDescription = customDescription || "Apply what you have learned with these interactive tools, comparisons, and glossary terms:";

  return (
    <section className={`related-tools-section ${compact ? "compact" : ""}`}>
      {!hideHeader && (
        <>
          <div className="related-tools-header">
            <Calculator size={20} className="header-icon" />
            {/* h2: this renders as a top-level section on calculator, blog and
                comparison pages (between other h2 sections) — h3 broke the outline. */}
            <h2 className="section-title">{displayTitle}</h2>
          </div>
          <p className="section-description">
            {displayDescription}
          </p>
        </>
      )}

      <div className="tools-grid">
        {filteredTools.map((tool, index) => {
          let dynamicHref = tool.href;

          if (tool.type === "calculator") {
            const slug = extractCalculatorSlug(tool.href);
            dynamicHref = getCalculatorUrl(slug, targetCountry);
          }
          // comparison keeps original href

          const config = TYPE_CONFIG[tool.type];

          return (
            <Link key={index} href={dynamicHref} className="tool-card-link">
              <article className="tool-card">
                <div
                  className="tool-badge"
                  style={{
                    background: config.color,
                    color: config.textColor,
                  }}
                >
                  {config.icon}
                  {config.label}
                </div>
                <h3 className="tool-title">{tool.title}</h3>
                <p className="tool-description">{tool.description}</p>
                <span className="tool-action" style={{ color: config.textColor }}>
                  {tool.type === "glossary"
                    ? "Read definition"
                    : tool.type === "guide"
                    ? "Read guide"
                    : "Try it now"}{" "}
                  <ArrowRight size={14} />
                </span>
              </article>
            </Link>
          );
        })}
      </div>

      <style jsx>{`
        .related-tools-section {
          margin: var(--space-8) 0;
          padding: var(--space-6);
          background: linear-gradient(135deg, var(--secondary-a04) 0%, var(--primary-a04) 100%);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
        }

        .related-tools-section.compact {
          margin: 0;
          padding: 0;
          border: none;
          background: none;
        }

        .related-tools-header {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          margin-bottom: var(--space-2);
        }

        .header-icon {
          color: var(--primary);
        }

        .section-title {
          font-family: var(--font-heading);
          font-size: var(--text-lg);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          margin: 0;
        }

        .section-description {
          font-size: var(--text-sm);
          color: var(--text-secondary);
          margin-bottom: var(--space-4);
          font-weight: var(--fw-medium);
          line-height: 1.5;
        }

        .tools-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: var(--space-4);
        }

        .tool-card-link {
          text-decoration: none;
          color: inherit;
          display: block;
        }

        .tool-card {
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: var(--space-4);
          height: 100%;
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
          transition: all 0.2s ease;
        }

        .tool-card:hover {
          transform: translateY(-2px);
          border-color: var(--primary);
          box-shadow: var(--shadow-lg);
        }

        .tool-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          font-size: var(--text-2xs);
          font-weight: var(--fw-bold);
          padding: 0.25rem 0.6rem;
          border-radius: var(--radius-sm);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          width: fit-content;
        }

        .tool-title {
          font-family: var(--font-heading);
          font-size: var(--text-base);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          margin: 0;
          line-height: 1.3;
        }

        .tool-description {
          font-size: var(--text-xs);
          color: var(--text-secondary);
          line-height: 1.5;
          flex-grow: 1;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .tool-action {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: var(--text-xs);
          font-weight: var(--fw-bold);
          margin-top: var(--space-2);
        }

        @media (max-width: 640px) {
          .tools-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
}
